(() => {
  const PRODUCT_SELECTOR = 'product-info[id^="MainProduct"]';
  const COLOUR_NAME_PATTERN = /(^|\b)(color|colour)(\b|$)|カラー|^色$/i;
  const MEDIA_TAG_PATTERN = /(?:^|[\[\]|;])\s*(?:colou?r|カラー|色)\s*[:：=]\s*([^\]|;]+)/i;
  const MEDIA_ROLE_PATTERN = /(?:^|[\[\]|;])\s*(?:type|role)\s*[:：=]\s*([^\]|;]+)/i;
  const SHARED_GROUPS = new Set(['all', 'shared', 'common', '共通']);
  const MEDIA_ROLE_PRIORITY = new Map([
    ['model', 0],
    ['still', 1],
    ['fabric', 2],
  ]);
  const PRIMARY_PRODUCT_CACHE = new Map();

  function normalize(value) {
    return String(value || '')
      .normalize('NFKC')
      .toLocaleLowerCase()
      .replace(/[\s\-_./\\]+/g, '')
      .replace(/[()\[\]{}'"“”‘’]/g, '')
      .trim();
  }

  function parseMediaColourText(value) {
    const match = String(value || '').match(MEDIA_TAG_PATTERN);
    return match ? normalize(match[1].trim()) : '';
  }

  function parseMediaColour(element) {
    const image = element?.querySelector('img');
    return parseMediaColourText(image?.getAttribute('alt') || '');
  }

  function parseMediaRole(element) {
    const image = element?.querySelector('img');
    const match = String(image?.getAttribute('alt') || '').match(MEDIA_ROLE_PATTERN);
    const role = match ? normalize(match[1].trim()) : '';
    return MEDIA_ROLE_PRIORITY.has(role) ? role : '';
  }

  function getSelectedColourContext(productInfo) {
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return { colour: '', input: null };

    const checkedInputs = Array.from(
      variantSelects.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')
    );

    for (const input of checkedInputs) {
      const name = input.getAttribute('name') || '';
      const fieldset = input.closest('fieldset');
      const legend = fieldset?.querySelector('legend')?.textContent || '';

      if (COLOUR_NAME_PATTERN.test(name) || COLOUR_NAME_PATTERN.test(legend)) {
        const mediaColour = parseMediaColourText(input.dataset.hyVariantMediaAlt);
        return { colour: mediaColour || normalize(input.value), input };
      }
    }

    const selects = Array.from(variantSelects.querySelectorAll('select'));

    for (const select of selects) {
      const name = select.getAttribute('name') || '';
      const label = variantSelects.querySelector(`label[for="${CSS.escape(select.id)}"]`)?.textContent || '';

      if (COLOUR_NAME_PATTERN.test(name) || COLOUR_NAME_PATTERN.test(label)) {
        const selectedOption = select.options[select.selectedIndex];
        const mediaColour = parseMediaColourText(selectedOption?.dataset.hyVariantMediaAlt);
        return { colour: mediaColour || normalize(select.value), input: selectedOption || select };
      }
    }

    return { colour: '', input: null };
  }

  function getSelectedVariant(productInfo) {
    const json = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
    if (!json) return null;

    try {
      return JSON.parse(json);
    } catch (error) {
      console.warn('HUIGON colour media: unable to read selected variant.', error);
      return null;
    }
  }

  function getSelectedVariantMediaId(productInfo, selectedVariant = getSelectedVariant(productInfo)) {
    const id = selectedVariant?.featured_media?.id || selectedVariant?.featured_image?.id;
    return id ? `${productInfo.dataset.section}-${id}` : '';
  }

  function buildPrimaryProductUrl(input) {
    const handle = input?.dataset.hyProductHandle || '';
    if (!handle) return '';

    const root = input.dataset.hyPrimaryLocaleRoot || '/';
    const cleanRoot = root === '/' ? '' : `/${root.replace(/^\/+|\/+$/g, '')}`;
    return `${window.location.origin}${cleanRoot}/products/${encodeURIComponent(handle)}.js`;
  }

  async function getPrimaryLocaleColour(input, selectedVariant) {
    const variantId = String(selectedVariant?.id || '');
    const optionPosition = Number.parseInt(input?.dataset.hyOptionPosition || '', 10);
    const url = buildPrimaryProductUrl(input);

    if (!variantId || !optionPosition || !url) return '';

    let productPromise = PRIMARY_PRODUCT_CACHE.get(url);
    if (!productPromise) {
      productPromise = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          PRIMARY_PRODUCT_CACHE.delete(url);
          console.warn('HUIGON colour media: unable to load primary-locale product data.', error);
          return null;
        });
      PRIMARY_PRODUCT_CACHE.set(url, productPromise);
    }

    const product = await productPromise;
    const primaryVariant = product?.variants?.find((variant) => String(variant.id) === variantId);
    return normalize(primaryVariant?.options?.[optionPosition - 1] || '');
  }

  function setHidden(element, hidden) {
    if (!element || element.hidden === hidden) return;
    element.hidden = hidden;
    element.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }

  function isSharedGroup(mediaColour) {
    return SHARED_GROUPS.has(mediaColour);
  }

  function getTaggedColours(items) {
    return Array.from(
      new Set(
        items
          .map((item) => parseMediaColour(item))
          .filter((colour) => colour && !isSharedGroup(colour))
      )
    );
  }

  function resolveSelectedColour(items, optionColour, selectedVariantMediaId) {
    const variantMedia = items.find((item) => item.dataset.mediaId === selectedVariantMediaId);
    const variantMediaColour = parseMediaColour(variantMedia);

    if (variantMediaColour && !isSharedGroup(variantMediaColour)) {
      return variantMediaColour;
    }

    const taggedColours = getTaggedColours(items);

    if (optionColour && taggedColours.includes(optionColour)) {
      return optionColour;
    }

    if (taggedColours.length === 1) {
      return taggedColours[0];
    }

    return '';
  }

  function sortItemsByRole(items) {
    const hasRoleTags = items.some((item) => Boolean(parseMediaRole(item)));
    if (!hasRoleTags) return items;

    return items
      .map((item, index) => {
        const role = parseMediaRole(item);
        return {
          item,
          index,
          priority: MEDIA_ROLE_PRIORITY.get(role) ?? 3,
        };
      })
      .sort((first, second) => first.priority - second.priority || first.index - second.index)
      .map(({ item }) => item);
  }

  function orderVisibleItems(items, selectedColour) {
    const matching = [];
    const shared = [];

    items.forEach((item) => {
      const mediaColour = parseMediaColour(item);
      if (mediaColour === selectedColour) matching.push(item);
      else if (isSharedGroup(mediaColour)) shared.push(item);
    });

    return sortItemsByRole([...matching, ...shared]);
  }

  function sameOrder(currentItems, targetItems) {
    if (currentItems.length !== targetItems.length) return false;
    return currentItems.every((item, index) => item === targetItems[index]);
  }

  function updateMainGallery(productInfo, selectedColour) {
    const gallery = productInfo.querySelector('media-gallery');
    const rail = gallery?.querySelector('.product__media-list');
    if (!gallery || !rail) return [];

    const items = Array.from(rail.querySelectorAll(':scope > .product__media-item'));
    const visibleItems = orderVisibleItems(items, selectedColour);
    const visibleSet = new Set(visibleItems);
    const currentVisibleItems = items.filter((item) => !item.hidden);

    items.forEach((item) => {
      setHidden(item, !visibleSet.has(item));
      item.classList.remove('is-active');
    });

    if (!sameOrder(currentVisibleItems, visibleItems)) {
      visibleItems.forEach((item) => rail.appendChild(item));
    }

    const firstVisible = visibleItems[0];
    if (firstVisible) firstVisible.classList.add('is-active');

    rail.scrollLeft = 0;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    viewer?.resetPages?.();

    const counterTotal = gallery.querySelector('.slider-counter--total');
    const counterCurrent = gallery.querySelector('.slider-counter--current');
    if (counterTotal) counterTotal.textContent = String(visibleItems.length);
    if (counterCurrent) counterCurrent.textContent = visibleItems.length ? '1' : '0';

    return visibleItems;
  }

  function scrollProductMediaToTop(productInfo) {
    const mediaWrapper =
      productInfo.querySelector('.product__media-wrapper') || productInfo.querySelector('media-gallery');
    if (!mediaWrapper) return;

    const top = window.scrollY + mediaWrapper.getBoundingClientRect().top;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function updateThumbnails(productInfo, visibleMediaIds, firstMediaId) {
    const thumbnailItems = Array.from(
      productInfo.querySelectorAll('.thumbnail-list__item[data-target]')
    );

    thumbnailItems.forEach((thumbnail) => {
      const visible = visibleMediaIds.has(thumbnail.dataset.target);
      setHidden(thumbnail, !visible);
      thumbnail.querySelector('button')?.removeAttribute('aria-current');
    });

    const firstVisibleThumbnail =
      thumbnailItems.find((item) => item.dataset.target === firstMediaId && !item.hidden) ||
      thumbnailItems.find((item) => !item.hidden);

    firstVisibleThumbnail?.querySelector('button')?.setAttribute('aria-current', 'true');
    productInfo.querySelector('[id^="GalleryThumbnails"]')?.resetPages?.();
  }

  function updateModal(productInfo, selectedColour) {
    const sectionId = productInfo.dataset.section;
    const modal = document.getElementById(`ProductModal-${sectionId}`);
    if (!modal) return;

    const modalItems = Array.from(
      modal.querySelectorAll('.product-media-modal__content > [data-media-id]')
    );
    const orderedVisibleItems = orderVisibleItems(modalItems, selectedColour);
    const visibleSet = new Set(orderedVisibleItems);
    const currentVisibleItems = modalItems.filter((item) => !item.hidden);

    modalItems.forEach((item) => setHidden(item, !visibleSet.has(item)));

    if (!sameOrder(currentVisibleItems, orderedVisibleItems)) {
      orderedVisibleItems.forEach((item) => item.parentElement.appendChild(item));
    }

    const dots = Array.from(modal.querySelectorAll('[data-product-modal-dot]'));
    dots.forEach((dot, index) => setHidden(dot, index >= orderedVisibleItems.length));

    const prev = modal.querySelector('[data-product-modal-prev]');
    const next = modal.querySelector('[data-product-modal-next]');
    const dotContainer = modal.querySelector('.product-media-modal__dots');
    const hideNavigation = orderedVisibleItems.length <= 1;

    setHidden(prev, hideNavigation);
    setHidden(next, hideNavigation);
    setHidden(dotContainer, hideNavigation);

    modal.currentIndex = 0;
    modal.mediaItems = orderedVisibleItems;
    modal.dots = dots.filter((dot) => !dot.hidden);
  }

  function restoreAllMedia(productInfo) {
    productInfo
      .querySelectorAll('.product__media-item, .thumbnail-list__item')
      .forEach((item) => setHidden(item, false));

    const modal = document.getElementById(`ProductModal-${productInfo.dataset.section}`);
    modal
      ?.querySelectorAll('.product-media-modal__content > [data-media-id], [data-product-modal-dot]')
      .forEach((item) => setHidden(item, false));
  }

  function markReady(productInfo) {
    productInfo.setAttribute('data-hy-colour-media-ready', 'true');
  }

  function showAllMediaSafely(productInfo, optionColour = '') {
    productInfo.removeAttribute('data-hy-colour-media-active');
    restoreAllMedia(productInfo);
    productInfo.dataset.hySelectedColour = '';
    productInfo.dataset.hySelectedOptionColour = optionColour;
    markReady(productInfo);
  }

  async function applyColourMedia(productInfo, force = false) {
    if (!productInfo?.isConnected) return;

    const requestId = String((Number(productInfo.dataset.hyColourMediaRequest || 0) + 1));
    productInfo.dataset.hyColourMediaRequest = requestId;

    const selectedVariant = getSelectedVariant(productInfo);
    const selectedVariantId = String(selectedVariant?.id || '');
    const { colour: localOptionColour, input: selectedInput } = getSelectedColourContext(productInfo);
    const selectedVariantMediaId = getSelectedVariantMediaId(productInfo, selectedVariant);
    const allGalleryItems = Array.from(
      productInfo.querySelectorAll('.product__media-list > .product__media-item')
    );
    const hasTaggedMedia = allGalleryItems.some((item) => Boolean(parseMediaColour(item)));

    if (!hasTaggedMedia) {
      showAllMediaSafely(productInfo, localOptionColour);
      return;
    }

    let optionColour = localOptionColour;
    let selectedColour = resolveSelectedColour(
      allGalleryItems,
      optionColour,
      selectedVariantMediaId
    );

    if (!selectedColour) {
      const primaryLocaleColour = await getPrimaryLocaleColour(selectedInput, selectedVariant);

      const latestVariantId = String(getSelectedVariant(productInfo)?.id || '');
      if (
        !productInfo.isConnected ||
        productInfo.dataset.hyColourMediaRequest !== requestId ||
        latestVariantId !== selectedVariantId
      ) {
        return;
      }

      if (primaryLocaleColour) {
        optionColour = primaryLocaleColour;
        selectedColour = resolveSelectedColour(
          allGalleryItems,
          optionColour,
          selectedVariantMediaId
        );
      }
    }

    const previousColour = productInfo.dataset.hySelectedColour || '';
    const shouldScrollToMediaTop = Boolean(previousColour && selectedColour !== previousColour);

    if (!selectedColour) {
      showAllMediaSafely(productInfo, optionColour);
      return;
    }

    if (!force && selectedColour === previousColour) {
      productInfo.dataset.hySelectedOptionColour = optionColour;
      markReady(productInfo);
      return;
    }

    productInfo.setAttribute('data-hy-colour-media-active', 'true');

    const visibleItems = updateMainGallery(productInfo, selectedColour);

    if (visibleItems.length === 0) {
      showAllMediaSafely(productInfo, optionColour);
      return;
    }

    const visibleMediaIds = new Set(visibleItems.map((item) => item.dataset.mediaId));
    const firstMediaId = visibleItems[0]?.dataset.mediaId || '';

    updateThumbnails(productInfo, visibleMediaIds, firstMediaId);
    updateModal(productInfo, selectedColour);

    productInfo.dataset.hySelectedColour = selectedColour;
    productInfo.dataset.hySelectedOptionColour = optionColour;

    if (shouldScrollToMediaTop) {
      if (window.matchMedia('(max-width: 749px)').matches && firstMediaId) {
        productInfo.querySelector('media-gallery')?.setActiveMedia?.(firstMediaId, false);
      } else {
        window.requestAnimationFrame(() => scrollProductMediaToTop(productInfo));
      }
    }

    productInfo.dispatchEvent(
      new CustomEvent('hy:colour-media-updated', {
        bubbles: true,
        detail: { colour: selectedColour, count: visibleItems.length },
      })
    );

    markReady(productInfo);
  }

  function bindProduct(productInfo) {
    if (!productInfo || productInfo.dataset.hyColourMediaBound === 'true') return;
    productInfo.dataset.hyColourMediaBound = 'true';
    applyColourMedia(productInfo, true);
  }

  function applyAfterVariantRefresh({ data } = {}) {
    const sectionId = String(data?.sectionId || '');
    const productInfo = Array.from(document.querySelectorAll(PRODUCT_SELECTOR)).find(
      (item) => !sectionId || item.dataset.section === sectionId
    );

    if (!productInfo) return;

    window.requestAnimationFrame(() => {
      applyColourMedia(productInfo, true);
    });
  }

  function bindVariantRefresh() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, applyAfterVariantRefresh);
  }

  function initialize(root = document) {
    root.querySelectorAll?.(PRODUCT_SELECTOR).forEach(bindProduct);
  }

  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target.closest?.(PRODUCT_SELECTOR) || event.target;
    bindProduct(productInfo);
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  bindVariantRefresh();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
})();